import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/shared";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { toast } from "sonner";

export default function Trends() {
  const { tenantId } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/panchatanthra/trends`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [tenantId]);

  return (
    <div className="space-y-10">
      <PageHeader
        testid="trends-header"
        eyebrow="Panchatanthra"
        title="Historical trends"
        description="Trends are drawn only from published snapshots. Version boundaries are flagged because comparability is not assumed."
      />
      {data?.series?.length ? (
        <>
          <div
            data-testid="comparability-banner"
            className={`border-l-2 px-5 py-4 text-sm ${
              data.comparable ? "border-success bg-success/5 text-success" : "border-warning bg-warning/5 text-warning"
            }`}
          >
            {data.note}
          </div>
          <div className="panel p-6" data-testid="trend-chart">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series}>
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis domain={[0, 5]} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid hsl(var(--border))" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="overall" stroke="hsl(var(--foreground))" strokeWidth={2} dot />
                  {data.element_codes.map((c, i) => (
                    <Line key={c} type="monotone" dataKey={c} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.4 + i * 0.1} strokeWidth={1} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="panel divide-y divide-border" data-testid="trend-table">
            {data.series.map((s, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <span className="text-sm">{s.label}</span>
                <span className="label-caps">version {s.version_label}</span>
                <span className="font-mono text-sm tabular-nums">{s.overall ?? "—"}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptyState testid="trends-empty" title="No snapshots" message="Publish at least one score snapshot to build a trend line." />
      )}
    </div>
  );
}
